import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import MenuItem, { MenuItemProps } from '../MenuItem';
import style from './style.module.scss';

require('@material/web/menu/menu.js');

export type VerticalMenuPositioning =
  | 'absolute'
  | 'fixed'
  | 'document'
  | 'popover';

export type VerticalMenuCorner =
  | 'end-start'
  | 'end-end'
  | 'start-start'
  | 'start-end';

export type VerticalMenuFocus =
  | 'none'
  | 'list-root'
  | 'first-item'
  | 'last-item';

export type VerticalMenuInteraction =
  | 'manual'
  | 'hover';

interface MaterialMenuElement extends HTMLElement {
  anchor: string;
  anchorElement: HTMLElement | null;
  anchorCorner: VerticalMenuCorner;
  menuCorner: VerticalMenuCorner;
  positioning: VerticalMenuPositioning;
  defaultFocus: VerticalMenuFocus;
  hasOverflow: boolean;
  open: boolean;
  quick: boolean;
  xOffset: number;
  yOffset: number;
  show: () => void;
  close: () => void;
}

export interface VerticalMenuHandle {
  show: () => void;
  close: () => void;
  getElement: () => MaterialMenuElement | null;
}

export interface VerticalMenuProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  children:
    | React.ReactElement<MenuItemProps>
    | React.ReactElement<MenuItemProps>[];
  anchor?: string;
  anchorElement?: HTMLElement | null;
  positioning?: VerticalMenuPositioning;
  anchorCorner?: VerticalMenuCorner;
  menuCorner?: VerticalMenuCorner;
  defaultFocus?: VerticalMenuFocus;
  xOffset?: number;
  yOffset?: number;
  quick?: boolean;
  hasOverflow?: boolean;
  interaction?: VerticalMenuInteraction;
  closeDelay?: number;
  onOpenChange?: (open: boolean) => void;
  onOpening?: (event: Event) => void;
  onOpened?: (event: Event) => void;
  onClosing?: (event: Event) => void;
  onClosed?: (event: Event) => void;
}

type VerticalMenuComponent = React.ForwardRefExoticComponent<
  VerticalMenuProps & React.RefAttributes<VerticalMenuHandle>
> & {
  Item: typeof MenuItem;
};

const VerticalMenu = forwardRef<VerticalMenuHandle, VerticalMenuProps>(
  (
    {
      children,
      anchor = '',
      anchorElement = null,
      positioning = 'absolute',
      anchorCorner = 'end-start',
      menuCorner = 'start-start',
      defaultFocus = 'first-item',
      xOffset = 0,
      yOffset = 0,
      quick = false,
      hasOverflow = false,
      interaction = 'manual',
      closeDelay = 100,
      onOpenChange,
      onOpening,
      onOpened,
      onClosing,
      onClosed,
      className = '',
      ...htmlAttributes
    },
    forwardedRef,
  ) => {
    const menuRef = useRef<MaterialMenuElement>(null);
    const closeTimeoutRef = useRef<number | null>(null);
    const classes = [style.verticalMenu, className]
      .filter(Boolean)
      .join(' ');

    useImperativeHandle(
      forwardedRef,
      () => ({
        show: () => menuRef.current?.show(),
        close: () => menuRef.current?.close(),
        getElement: () => menuRef.current,
      }),
      [],
    );

    useEffect(() => {
      const menu = menuRef.current;

      if (!menu) {
        return;
      }

      menu.anchor = anchor;
      menu.anchorElement = anchorElement;
      menu.positioning = positioning;
      menu.anchorCorner = anchorCorner;
      menu.menuCorner = menuCorner;
      menu.defaultFocus = defaultFocus;
      menu.xOffset = xOffset;
      menu.yOffset = yOffset;
      menu.quick = quick;
      menu.hasOverflow = hasOverflow;
    }, [
      anchor,
      anchorElement,
      positioning,
      anchorCorner,
      menuCorner,
      defaultFocus,
      xOffset,
      yOffset,
      quick,
      hasOverflow,
    ]);

    useEffect(() => {
      const menu = menuRef.current;

      if (!menu || interaction !== 'hover') {
        return;
      }

      const anchorElement = menu.anchorElement;

      if (!anchorElement) {
        return;
      }

      const interactionArea = anchorElement.parentElement || anchorElement;

      const clearCloseTimeout = () => {
        if (closeTimeoutRef.current !== null) {
          window.clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
      };

      const closeOtherMenus = () => {
        document
          .querySelectorAll<MaterialMenuElement>(
            `md-menu.${style.verticalMenu}`,
          )
          .forEach((otherMenu) => {
            if (otherMenu !== menu && otherMenu.open) {
              otherMenu.close();
            }
          });
      };

      const showMenu = (focus: VerticalMenuFocus) => {
        clearCloseTimeout();
        closeOtherMenus();
        menu.defaultFocus = focus;

        if (!menu.open) {
          menu.show();
        }
      };

      const handleMouseEnter = () => {
        showMenu('none');
      };

      const handleMouseLeave = () => {
        clearCloseTimeout();
        closeTimeoutRef.current = window.setTimeout(() => {
          menu.close();
          closeTimeoutRef.current = null;
        }, closeDelay);
      };

      const handleAnchorKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          showMenu('first-item');
        } else if (event.key === 'Escape' && menu.open) {
          event.preventDefault();
          menu.close();
        }
      };

      anchorElement.setAttribute('aria-expanded', String(menu.open));
      interactionArea.addEventListener('mouseenter', handleMouseEnter);
      interactionArea.addEventListener('mouseleave', handleMouseLeave);
      anchorElement.addEventListener('keydown', handleAnchorKeyDown);

      return () => {
        clearCloseTimeout();
        interactionArea.removeEventListener('mouseenter', handleMouseEnter);
        interactionArea.removeEventListener('mouseleave', handleMouseLeave);
        anchorElement.removeEventListener('keydown', handleAnchorKeyDown);
      };
    }, [anchor, anchorElement, closeDelay, interaction]);

    useEffect(() => {
      const menu = menuRef.current;

      if (!menu) {
        return;
      }

      const handleOpening = (event: Event) => {
        menu.anchorElement?.setAttribute('aria-expanded', 'true');
        onOpening?.(event);
      };
      const handleOpened = (event: Event) => {
        onOpened?.(event);
        onOpenChange?.(true);
      };
      const handleClosing = (event: Event) => onClosing?.(event);
      const handleClosed = (event: Event) => {
        menu.anchorElement?.setAttribute('aria-expanded', 'false');
        onClosed?.(event);
        onOpenChange?.(false);
      };

      menu.addEventListener('opening', handleOpening);
      menu.addEventListener('opened', handleOpened);
      menu.addEventListener('closing', handleClosing);
      menu.addEventListener('closed', handleClosed);

      return () => {
        menu.removeEventListener('opening', handleOpening);
        menu.removeEventListener('opened', handleOpened);
        menu.removeEventListener('closing', handleClosing);
        menu.removeEventListener('closed', handleClosed);
      };
    }, [onOpenChange, onOpening, onOpened, onClosing, onClosed]);

    return React.createElement(
      'md-menu',
      {
        ...htmlAttributes,
        class: classes,
        ref: menuRef,
      },
      children,
    );
  },
) as VerticalMenuComponent;

VerticalMenu.Item = MenuItem;

export default VerticalMenu;
